import { UpgradeModel } from "types";

import aegisCoreImage from "assets/upgrade-aegis-core.svg";
import alphaReactorImage from "assets/upgrade-alpha-reactor.svg";
import apexCoreImage from "assets/upgrade-apex-core.svg";
import burstMatrixImage from "assets/upgrade-burst-matrix.svg";
import dashMatrixImage from "assets/upgrade-dash-matrix.svg";
import eclipseCoreImage from "assets/upgrade-eclipse-core.svg";
import fluxScopeImage from "assets/upgrade-flux-scope.svg";
import furyReactorImage from "assets/upgrade-fury-reactor.svg";
import nebulaCoreImage from "assets/upgrade-nebula-core.svg";
import novaDriveImage from "assets/upgrade-nova-drive.svg";
import nullBarrierImage from "assets/upgrade-null-barrier.png";
import nullBladeImage from "assets/upgrade-null-blade.svg";
import nullShieldImage from "assets/upgrade-null-shield.png";
import omegaCoreImage from "assets/upgrade-omega-core.svg";
import phaserSightImage from "assets/upgrade-phaser-sight.svg";
import plasmaDriveImage from "assets/upgrade-plasma-drive.svg";
import ravagerCoreImage from "assets/upgrade-ravager-core.svg";
import reaperCoreImage from "assets/upgrade-reaper-core.svg";
import sightMatrixImage from "assets/upgrade-sight-matrix.svg";
import tracerMatrixImage from "assets/upgrade-tracer-matrix.svg";
import voidBladeImage from "assets/upgrade-void-blade.svg";
import wardNexusImage from "assets/upgrade-ward-nexus.svg";

import {
  attackAnimation,
  reactorCoreAnimation,
  shieldsAnimation,
  targetingSystemAnimation,
  thrusterAnimation,
} from "components/styles/animations";
import {
  DEFAULT_REACTOR_SPEED,
  DEFAULT_TARGETING_SPEED,
  DEFAULT_THRUSTERS_SPEED,
  DEFAULT_WEAPONS_SPEED,
} from "../constants";

const SENECA_UNDERHAVEN_UPGRADES: Record<string, UpgradeModel> = {
  // WEAPONS
  null_blade: {
    slug: "null_blade",
    name: "Null Blade",
    description:
      "An antimatter laser that weakens ship parts and increases your max damage and increases your pilot skill power.",
    image: nullBladeImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 10,
      energy: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
      fasterRecharge: 1,
    },
  },
  void_blade: {
    slug: "void_blade",
    name: "Void Blade",
    description:
      "A powerful antimatter laser that increases your max damage and also your pilot skill power/speed.",
    image: voidBladeImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 19,
      energy: 10,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 1,
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  // SHIELDS
  null_shield: {
    slug: "null_shield",
    name: "Null Shield",
    description:
      "An antimatter force field that increases ship health and recharges your pilot skills faster and makes them more powerful.",
    image: nullShieldImage,
    animation: shieldsAnimation,
    animationSpeed: 2400,
    part: "shields",
    requirements: {
      resilience: 14,
      energy: 7,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 1,
      energyMultiplier: 0.75,
      fasterRecharge: 0.25,
    },
  },
  null_barrier: {
    slug: "null_barrier",
    name: "Null Barrier",
    description:
      "A powerful antimatter force field increases your ship's health and shields as well as your pilot skill power.",
    image: nullBarrierImage,
    animation: shieldsAnimation,
    animationSpeed: 2400,
    part: "shields",
    requirements: {
      resilience: 13,
      energy: 7,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 0.5,
      damageReduction: 0.5,
      energyMultiplier: 1,
    },
  },
  // THRUSTERS
  plasma_drive: {
    slug: "plasma_drive",
    name: "Plasma Drive",
    description:
      "Special energy thrusters that elevate your ability to attack faster, avoid attacks, and increases your pilot skill power.",
    image: plasmaDriveImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 12,
      energy: 6,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 0.5,
      attackEvasion: 0.5,
      fasterRecharge: 1,
    },
  },
  nova_drive: {
    slug: "nova_drive",
    name: "Nova Drive",
    description:
      "Powerful energy thrusters that elevate the frequency of your attacks as well as recharges your pilot skills faster. Speed on speed.",
    image: novaDriveImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 11,
      energy: 6,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 1,
      fasterRecharge: 1,
    },
  },
  // TARGETING SYSTEMS
  flux_scope: {
    slug: "flux_scope",
    name: "Flux Scope",
    description:
      "An antimatter-based targeting system upgrade that helps you land attacks more consistently and increases your pilot skill power.",
    image: fluxScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 8,
      energy: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 1,
      energyMultiplier: 1,
    },
  },
  phaser_sight: {
    slug: "phaser_sight",
    name: "Phaser Sight",
    description:
      "An antimatter-based targeting system upgrade that helps you land attacks more consistently and increases your pilot skill power.",
    image: phaserSightImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 16,
      energy: 8,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 1,
      energyMultiplier: 1,
    },
  },
  // REACTOR CORES
  alpha_reactor: {
    slug: "alpha_reactor",
    name: "Alpha Reactor",
    description:
      "A decently powerful upgrade to your reactor core that boosts your skill recharging speed and power.",
    image: alphaReactorImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 23,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  fury_reactor: {
    slug: "fury_reactor",
    name: "Fury Reactor",
    description:
      "A quite powerful upgrade to your reactor core that really boosts your skill power.",
    image: furyReactorImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 29,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
    },
  },
  dash_matrix: {
    slug: "dash_matrix",
    name: "Dash Matrix",
    description:
      "A powerful upgrade to your reactor core that boosts your skill power and attack/evasion speed.",
    image: dashMatrixImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 18,
      speed: 9,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
  },
  burst_matrix: {
    slug: "burst_matrix",
    name: "Burst Matrix",
    description:
      "A powerful upgrade to your reactor core that boosts your skill recharging speed/power and attack speed.",
    image: burstMatrixImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 20,
      speed: 10,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
      attackSpeed: 1,
    },
  },
  ward_nexus: {
    slug: "ward_nexus",
    name: "Ward Nexus",
    description:
      "A powerful upgrade to your reactor core that boosts your pilot skill power and ship parts protection.",
    image: wardNexusImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 22,
      resilience: 11,
    },
    grade: "elite",
    derivedStatsModifiers: {
      fasterRecharge: 1,
      weakenPartsReduction: 1,
    },
  },
  aegis_core: {
    slug: "aegis_core",
    name: "Aegis Core",
    description:
      "An extremely powerful upgrade to your reactor core that boosts your skill recharging speed and also deflects damage.",
    image: aegisCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 24,
      resilience: 12,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      damageReduction: 1,
    },
  },
  sight_matrix: {
    slug: "sight_matrix",
    name: "Sight Matrix",
    description:
      "A powerful upgrade to your reactor core that boosts your skill recharging speed/power and minimum damage.",
    image: sightMatrixImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 27,
      precision: 14,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
      minAttackDamage: 1,
    },
  },
  tracer_matrix: {
    slug: "tracer_matrix",
    name: "Tracer Matrix",
    description:
      "A powerful upgrade to your reactor core that boosts your skill power and attack accuracy and minimum damage.",
    image: tracerMatrixImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 29,
      precision: 15,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      attackAccuracy: 0.5,
      minAttackDamage: 0.5,
    },
  },
  reaper_core: {
    slug: "reaper_core",
    name: "Reaper Core",
    description:
      "A powerful upgrade to your reactor core that boosts your skill power/speed and ability to weaken opponent parts.",
    image: reaperCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 33,
      firepower: 17,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
      maxWeakenParts: 1,
    },
  },
  ravager_core: {
    slug: "ravager_core",
    name: "Ravager Core",
    description:
      "A very powerful upgrade to your reactor core that boosts your skill power and max attack damage.",
    image: ravagerCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 36,
      firepower: 18,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      maxAttackDamage: 1,
    },
  },
  nebula_core: {
    slug: "nebula_core",
    name: "Nebula Core",
    description:
      "A quite powerful upgrade to your reactor core that boosts the speed and power of your abilities.",
    image: nebulaCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 41,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.25,
      fasterRecharge: 0.75,
    },
  },
  eclipse_core: {
    slug: "eclipse_core",
    name: "Eclipse Core",
    description:
      "An immensely powerful upgrade to your reactor core that boosts the power and speed of your abilities.",
    image: eclipseCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 45,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.75,
      fasterRecharge: 0.25,
    },
  },
  apex_core: {
    slug: "apex_core",
    name: "Apex Core",
    description:
      "I would wear gloves when installing this core. It's a bit volatile.",
    image: apexCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 50,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  omega_core: {
    slug: "omega_core",
    name: "Omega Ω Core",
    description:
      "Wow this core is special. So special it has a symbol in its name.",
    image: omegaCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 55,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
    },
  },
};

export default SENECA_UNDERHAVEN_UPGRADES;
